import { IStore } from 'redux/IStore';
import moment = require('moment');
import { Dispatch } from 'react-redux';
import { lastMonday, momentWithTimezone, normalizeDate } from '../helpers';
import {
	IBookingTimeTable,
	IBookingTimeTableGroup,
	IBookingTimetablesAction,
	IBookingTimetablesState,
} from 'models/shop/bookingtimetables';
import { IScheduleFilters } from 'models/shop/schedule';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import { getResourceTimetables, postBookingTimetable, postPartiallyUnavailableBookingTimetable } from './helpers';
import { handleSettledPromises, requestHelper } from 'redux/helpers';
import { combineDateAndTime, timeAsNumber } from 'helpers/bookingTemplates';
import { IManualAppointment } from 'models/shop/appointments';
import { uniq } from 'helpers/common';
import { IBlockedTimeFormData } from 'models/shop/manualbooking';

/** Action Types */
export const REQUEST_START: string = 'bookingtimetables/REQUEST_START';
export const REQUEST_FAILURE: string = 'bookingtimetables/REQUEST_FAILURE';
export const GET_BOOKINGTIMETABLES_SUCCESS: string = 'bookingtimetables/GET_BOOKINGTIMETABLES_SUCCESS';
export const SET_FILTERS: string = 'bookingtimetables/SET_FILTERS';
export const CLEAR_ERRORS: string = 'bookingtimetables/CLEAR_ERRORS';

/** Initial State */
const getinitialState = (): IBookingTimetablesState => ({
	items: null,
	memberId: null,
	officeId: null,
	date: lastMonday(moment()),
});

/** Reducer */
export function bookingTimetablesReducer(
	state = getinitialState(),
	action: IBookingTimetablesAction,
): IBookingTimetablesState {
	switch (action.type) {
		case REQUEST_START:
			return {
				...state,
				isFetching: true,
				error: false,
			};

		case REQUEST_FAILURE:
			return {
				...state,
				isFetching: false,
				message: action.payload.message,
				error: true,
			};

		case GET_BOOKINGTIMETABLES_SUCCESS:
			return {
				...state,
				isFetching: false,
				items: action.payload.items,
			};

		case CLEAR_ERRORS:
			return {
				...state,
				error: false,
			};

		case SET_FILTERS:
			if (!action.payload) {
				return state;
			}
			const keys: Array<keyof IBookingTimetablesAction['payload']> = ['date', 'memberId', 'officeId'];
			const filters: IBookingTimetablesAction['payload'] = {};
			for (const key of keys) {
				if (key in action.payload && action.payload[key] !== undefined) {
					filters[key] = action.payload[key];
				}
			}
			return {
				...state,
				...filters,
			};

		default:
			return state;
	}
}

export function setBookingTimetablesFiltersOfficeAppointmentsDate(
	officeId: IBookingTimetablesState['officeId'] | undefined,
) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		return dispatch(
			setBookingTimetablesFiltersOffice(officeId, { date: moment(getState().appointments.date).toISOString() }),
		);
	};
}

export function setBookingTimetablesFiltersOffice(
	officeId: IBookingTimetablesState['officeId'] | undefined,
	filters?: any,
) {
	return setBookingTimetablesFiltersAndFetch({ officeId, memberId: null, ...filters });
}

export function setBookingTimetablesFiltersMember(memberId: IBookingTimetablesState['memberId'] | undefined) {
	return setBookingTimetablesFiltersAndFetch({ memberId });
}

export function setBookingTimetablesFiltersDate(date: string | Date) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		const timezone = getState().manageShop?.shop?.offices?.find((o) => o.id === getState().bookingTimetables.officeId)
			?.zoneInfo;
		const filters: Partial<IScheduleFilters> = { date: normalizeDate(date, 'week', timezone) };
		dispatch(setBookingTimetablesFiltersAndFetch(filters));
	};
}

function setBookingTimetablesFiltersAndFetch(filters: Partial<IScheduleFilters>) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		const officeId = filters.officeId || getState().bookingTimetables.officeId;
		const timezone = getState().manageShop?.shop?.offices?.find((o) => o.id === officeId)?.zoneInfo;
		let date = filters.date || getState().bookingTimetables.date || moment().toISOString();
		date = normalizeDate(date, 'week', timezone);
		dispatch({
			type: SET_FILTERS,
			payload: {
				...filters,
				date,
			},
		});
		return dispatch(getBookingTimetables());
	};
}

function getMultipleResourcesBookingTimetables(resources: string[], timezone?: string) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		const filters = getState().bookingTimetables;
		const shopId = getState().manageShop?.shop?.id;
		if (!shopId) {
			console.warn('Booking timetables shop or resource filter not set', { shopId, filters });
			return Promise.resolve(false);
		}
		dispatch(requestStart());
		return Promise.all(
			resources.map((memberId) =>
				getResourceTimetables(getState, memberId, [
					momentWithTimezone(timezone, filters.date).startOf('day'),
					momentWithTimezone(timezone, filters.date).add(6, 'days').endOf('day'),
				]),
			),
		)
			.then((timetableGroups) => [].concat.apply([], timetableGroups)) //flatten array
			.then((res: IBookingTimeTable[]) => dispatch(getBookingTimetablesSuccess(res)))
			.catch((err) => dispatch(requestFailure(err)));
	};
}

/** Async Action Creator */
export function getBookingTimetables() {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		const filters = getState().bookingTimetables;
		const shop = getState().manageShop?.shop;
		const shopId = shop?.id;
		if (!shopId) {
			console.warn('Booking timetables shop or resource filter not set', { shopId, filters });
			return Promise.resolve(false);
		}
		if (!filters.memberId && filters.officeId && shop) {
			const office = shop.offices?.find((o) => o.id === filters.officeId);
			if (office?.staff) {
				const memberIds = office.staff.map((s) => s.id);
				const timezone = office?.zoneInfo;
				return dispatch(getMultipleResourcesBookingTimetables(memberIds, timezone));
			}
		}
		dispatch(requestStart());
		return getResourceTimetables(getState, filters.memberId)
			.then((res) => dispatch(getBookingTimetablesSuccess(res)))
			.catch((err) => dispatch(requestFailure(err)));
	};
}

export function deleteBookingTimetableGroup(group: IBookingTimeTableGroup, refreshOnSuccess = true) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		dispatch(requestStart());
		if (!group.bookingTimetables?.length) {
			console.warn('Trying to delete booking timetable group without timetables :S');
			return Promise.resolve(false);
		}
		return Promise.allSettled(
			group.bookingTimetables.map((tt) =>
				requestHelper(
					fetch(getState().fetchDefault.baseAPI + 'api/shop/resourceTimeTables/' + tt.id, {
						...getState().fetchDefault.requestInit,
						method: 'DELETE',
					}),
				),
			),
		).then((settled) =>
			handleSettledPromises(
				settled,
				(e) => dispatch(requestFailure(e)),
				refreshOnSuccess ? () => dispatch(getBookingTimetables()) : undefined,
			),
		);
	};
}

export function bookingTemplateResourceIds(bt: IServicesList) {
	return (bt.resources || []).map((res) => (typeof res === 'object' ? res.id : res));
}

export function createBookingTimetable(
	officeId: string,
	resourceId: string,
	startTime: IBookingTimeTable['startTime'],
	endTime: IBookingTimeTable['endTime'],
	existingForDay: IBookingTimeTable[],
	refreshOnSuccess = true,
	bookingTemplatesWithResource: IServicesList[],
	timezone?: string,
) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		dispatch(requestStart());
		const mStartTime = momentWithTimezone(timezone, startTime);
		const mEndTime = momentWithTimezone(timezone, endTime);
		const nStartTime = timeAsNumber(mStartTime);
		const nEndTime = timeAsNumber(mEndTime);
		if (mStartTime.isSameOrAfter(mEndTime)) {
			dispatch(requestFailure('Start time after end time'));
			return Promise.reject('Start time after end time');
		}
		if (!mStartTime.isSame(mEndTime, 'day')) {
			dispatch(requestFailure('Start and end time different days'));
			return Promise.reject('Start and end time different days');
		}
		const existingToDelete = existingForDay.filter((ett) => {
			if (ett.resourceId !== resourceId) {
				return false;
			}
			const start = momentWithTimezone(timezone, ett.startTime);
			const end = momentWithTimezone(timezone, ett.endTime);
			if (!start.isSame(mStartTime, 'day')) {
				return false;
			}
			const nStart = timeAsNumber(start);
			const nEnd = timeAsNumber(end);
			if (nStart > nEndTime || nStartTime > nEnd) {
				return false;
			}
			return true;
		});

		const bookingTimeTables = bookingTemplatesWithResource.map(
			(template) =>
				({
					id: undefined,
					officeId,
					bookingTemplateId: template.id,
					resourceId,
					startTime,
					endTime,
				}) as IBookingTimeTable,
		);

		return Promise.allSettled([
			postBookingTimetable(getState, bookingTimeTables),
			...existingToDelete.map((etd) =>
				requestHelper(
					fetch(getState().fetchDefault.baseAPI + 'api/shop/resourceTimeTables/' + etd.id, {
						...getState().fetchDefault.requestInit,
						method: 'DELETE',
					}),
				),
			),
		]).then((settled) =>
			handleSettledPromises(
				settled,
				(e) => dispatch(requestFailure(e)),
				refreshOnSuccess ? () => dispatch(getBookingTimetables()) : undefined,
			),
		);
	};
}

/**
 *
 * @param officeId
 * @param resourceId
 * @param startTime
 * @param endTime
 * @param bookingTemplatesWithResource
 * @returns builds and returns multiple booking time tables (all of resource's booking templates)
 */
export function generateMultipleBookingTimeTables(
	officeId: string,
	resourceId: string,
	startTime: IBookingTimeTable['startTime'],
	endTime: IBookingTimeTable['endTime'],
	bookingTemplatesWithResource: IServicesList[],
	timezone?: any,
): IBookingTimeTable[] {
	const mStartTime = momentWithTimezone(timezone, startTime);
	const mEndTime = momentWithTimezone(timezone, endTime);

	if (mStartTime.isSameOrAfter(mEndTime)) {
		console.error('Start time after end time');
		return [];
	}
	if (!mStartTime.isSame(mEndTime, 'day')) {
		console.error('Start and end time different days');
		return [];
	}

	const bookingTimeTables = bookingTemplatesWithResource.map(
		(template) =>
			({
				id: undefined,
				officeId,
				bookingTemplateId: template.id,
				resourceId,
				startTime,
				endTime,
			}) as IBookingTimeTable,
	);

	return bookingTimeTables;
}

/**
 *
 * @param officeId
 * @param resourceId
 * @param startTime
 * @param endTime
 * @returns builds and returns a single partially unavailable time table (no booking template id's)
 */
export function generatePartiallyUnavailableBookingTimeTable(
	officeId: string,
	resourceId: string,
	startTime: IBookingTimeTable['startTime'],
	endTime: IBookingTimeTable['endTime'],
	timezone?: any,
): IBookingTimeTable {
	const mStartTime = momentWithTimezone(timezone, startTime);
	const mEndTime = momentWithTimezone(timezone, endTime);

	if (mStartTime.isSameOrAfter(mEndTime)) {
		console.error('Start time after end time');
		return null;
	}
	if (!mStartTime.isSame(mEndTime, 'day')) {
		console.error('Start and end time different days');
		return null;
	}

	const partiallyUnavailableTimeTable = {
		id: undefined,
		officeId: officeId,
		resourceId: resourceId,
		startTime,
		endTime,
	} as IBookingTimeTable;

	return partiallyUnavailableTimeTable;
}

/**
 *
 * @param refreshOnSuccess fetch booking time tables if needed
 * @param bookingTimeTables booking time tables for each booking template id of resource
 * @returns
 */
export function createMultipleBookingTimetables(refreshOnSuccess = true, bookingTimeTables: IBookingTimeTable[]) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		dispatch(requestStart());

		return Promise.allSettled([postBookingTimetable(getState, bookingTimeTables)]).then((settled) =>
			handleSettledPromises(
				settled,
				(e) => dispatch(requestFailure(e)),
				refreshOnSuccess ? () => dispatch(getBookingTimetables()) : undefined,
			),
		);
	};
}

/**
 *
 * @param refreshOnSuccess fetch booking time tables if needed
 * @param partiallyUnavailableTimeTables partially unavailable time tables (no booking template id)
 * @returns returns promise with post for all the partiallyUnavailableTimeTables sent
 */
export function createMultiplePartiallyUnavailableBookingTimetables(
	refreshOnSuccess = true,
	partiallyUnavailableTimeTables: IBookingTimeTable[],
) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		dispatch(requestStart());

		return Promise.allSettled([
			postPartiallyUnavailableBookingTimetable(getState, partiallyUnavailableTimeTables),
		]).then((settled) =>
			handleSettledPromises(
				settled,
				(e) => dispatch(requestFailure(e)),
				refreshOnSuccess ? () => dispatch(getBookingTimetables()) : undefined,
			),
		);
	};
}

/**
 * deletes all time tables (except UnavailableTimeTable) in the given time span for the specified resource
 * @param startTime
 * @param endTime
 * @param resourceId
 * @returns
 */
export function deleteAllTimeTablesBetweenTimes(startTime: string, endTime: string, resourceId: string) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		if (!startTime || !endTime || !resourceId) {
			return Promise.resolve(false);
		}

		dispatch(requestStart());

		return requestHelper(
			fetch(getState().fetchDefault.baseAPI + 'api/shop/ResourceTimeTables/', {
				...getState().fetchDefault.requestInit,
				method: 'DELETE',
				body: JSON.stringify({ dateFrom: startTime, dateTo: endTime, resourceId }),
			}),
		)
			.then(() => dispatch(getBookingTimetables()))
			.then(() => Promise.resolve(true))
			.catch((err) => {
				console.error(err);
				dispatch(requestFailure(err));
				return Promise.resolve(false);
			});
	};
}

/**
 * creates a custom time event (prevents all other booking templates to be booked in the given time slot)
 * @param dispatch
 * @param getState
 * @param base includes startTime, endTime, officeId, resourceId and name of custom time event ("0", "1", "2")
 * @returns
 */
export function createUnavailableTimeTable(
	dispatch: Dispatch<IStore>,
	getState: () => IStore,
	base: Omit<IBookingTimeTable, 'bookingTemplateId'>,
): Promise<boolean> {
	dispatch(requestStart());
	return Promise.all([
		requestHelper(
			fetch(getState().fetchDefault.baseAPI + 'api/shop/UnavailableTimeTables', {
				...getState().fetchDefault.requestInit,
				method: 'POST',
				body: JSON.stringify({ ...base }),
			}),
		),
	])
		.then(() => dispatch(getBookingTimetables()))
		.then(() => true)
		.catch((err) => {
			console.error(err);
			dispatch(requestFailure(err));
			return false;
		});
}

// Old function for creating custom time events (new one no longer requires bookingTemplateId's)
function createTimetables(
	dispatch: Dispatch<IStore>,
	getState: () => IStore,
	base: Omit<IBookingTimeTable, 'bookingTemplateId'>,
	bookingTemplateIds: string[],
): Promise<boolean> {
	if (!bookingTemplateIds.length) {
		return Promise.resolve(true);
	}
	dispatch(requestStart());
	return Promise.all(
		bookingTemplateIds.map((bookingTemplateId) =>
			requestHelper(
				fetch(getState().fetchDefault.baseAPI + 'api/shop/UnavailableTimeTables', {
					...getState().fetchDefault.requestInit,
					method: 'POST',
					body: JSON.stringify({ ...base, bookingTemplateId }),
				}),
			),
		),
	)
		.then(() => dispatch(getBookingTimetables()))
		.then(() => true)
		.catch((err) => {
			console.error(err);
			dispatch(requestFailure(err));
			return false;
		});
}

export function addOtherScheduleTime(
	officeId: string,
	resourceId: string,
	form: Omit<IManualAppointment, 'childrenAddBookingItemRequest'>,
	day: moment.Moment,
	timezone: string,
) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<boolean> => {
		const startTime = combineDateAndTime(day, momentWithTimezone(timezone, form.schedule), true);
		const endTime = combineDateAndTime(day, momentWithTimezone(timezone, form.scheduleTo), true);
		const timetableRequest: Omit<IBookingTimeTable, 'bookingTemplateId'> = {
			resourceId,
			officeId,
			startTime: startTime.toISOString(),
			endTime: endTime.toISOString(),
			name: String(form.timeSlotType),
		};

		return createUnavailableTimeTable(dispatch, getState, timetableRequest);
	};
}

export function addBlockedTime(form: IBlockedTimeFormData, officeId: string, timezone: string) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<boolean> => {
		const startTime = combineDateAndTime(
			momentWithTimezone(timezone, form.date),
			momentWithTimezone(timezone, form.startTime),
			true,
		);
		const endTime = combineDateAndTime(
			momentWithTimezone(timezone, form.date),
			momentWithTimezone(timezone, form.endTime),
			true,
		);
		const timetableRequest: Omit<IBookingTimeTable, 'bookingTemplateId'> = {
			resourceId: form.resourceId,
			officeId,
			startTime: startTime.toISOString(),
			endTime: endTime.toISOString(),
			name: form?.name || '3',
			description: form.note,
		};

		return createUnavailableTimeTable(dispatch, getState, timetableRequest);
	};
}

export function moveBlockedTime(existingTimetables: IBookingTimeTable[], form: IBlockedTimeFormData, timezone: string) {
	// Filter duplicates by id (for old logic custom time events to work)
	const filteredExistingTimeTables = existingTimetables.filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i);

	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<boolean> => {
		const startTime = combineDateAndTime(
			momentWithTimezone(timezone, form.date),
			momentWithTimezone(timezone, form.startTime),
		);
		const endTime = combineDateAndTime(
			momentWithTimezone(timezone, form.date),
			momentWithTimezone(timezone, form.endTime),
		);
		const byResource: { [resourceId: string]: IBookingTimeTable[] } = {};
		const officeIds = uniq(filteredExistingTimeTables.map((tt) => tt.officeId));
		if (officeIds.length > 1) {
			console.error('Multiple Office IDs!');
			return Promise.resolve(false);
		}
		for (const timeTable of filteredExistingTimeTables) {
			if (!(timeTable.resourceId in byResource)) {
				byResource[timeTable.resourceId] = [];
			}
			byResource[timeTable.resourceId].push(timeTable);
		}
		return Promise.all(
			Object.keys(byResource).map((resourceId) => {
				const timetables = byResource[resourceId];
				if (!timetables.length) {
					return true;
				}
				const timetableRequest: Omit<IBookingTimeTable, 'bookingTemplateId'> = {
					resourceId: form.resourceId,
					officeId: timetables[0].officeId,
					startTime: startTime.toISOString(),
					endTime: endTime.toISOString(),
					name: form?.name || '3',
					description: form.note,
				};

				const btIds = uniq(timetables.map((tt) => tt.bookingTemplateId));

				if (btIds.length > 0) {
					return createTimetables(dispatch, getState, timetableRequest, btIds);
				} else {
					return createUnavailableTimeTable(dispatch, getState, timetableRequest);
				}
			}),
		).then((results): boolean | Promise<boolean> => {
			if (results.some((r) => r === false)) {
				return false;
			}
			dispatch(requestStart());
			return Promise.all(
				filteredExistingTimeTables.map((tt) =>
					requestHelper(
						fetch(getState().fetchDefault.baseAPI + 'api/shop/ResourceTimeTables/' + tt.id, {
							...getState().fetchDefault.requestInit,
							method: 'DELETE',
						}),
					),
				),
			)
				.then(() => dispatch(getBookingTimetables()))
				.then(() => true)
				.catch((err) => {
					console.error(err);
					dispatch(requestFailure(err));
					return false;
				});
		});
	};
}

export function deleteOtherScheduleTime(existingTimetables: IBookingTimeTable[]) {
	// Filter duplicates by id (for old logic custom time events to work)
	const filteredExistingTimeTables = existingTimetables.filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i);

	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<boolean> => {
		if (!filteredExistingTimeTables.length) {
			return Promise.resolve(true);
		}
		dispatch(requestStart());
		return Promise.all(
			filteredExistingTimeTables.map((tt) =>
				requestHelper(
					fetch(getState().fetchDefault.baseAPI + 'api/shop/ResourceTimeTables/' + tt.id, {
						...getState().fetchDefault.requestInit,
						method: 'DELETE',
					}),
				),
			),
		)
			.then(() => dispatch(getBookingTimetables()))
			.then(() => true)
			.catch((err) => {
				console.error(err);
				dispatch(requestFailure(err));
				return false;
			});
	};
}

export function moveOtherScheduleTime(
	existingTimetables: IBookingTimeTable[],
	form: Pick<IManualAppointment, 'schedule' | 'scheduleTo'>,
	day: moment.Moment,
	timezone?: string,
) {
	// Filter duplicates by id (for old logic custom time events to work)
	const filteredExistingTimeTables = existingTimetables.filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i);

	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<boolean> => {
		const startTime = combineDateAndTime(day, momentWithTimezone(timezone, form.schedule));
		const endTime = combineDateAndTime(day, momentWithTimezone(timezone, form.scheduleTo));
		const byResource: { [resourceId: string]: IBookingTimeTable[] } = {};
		const officeIds = uniq(filteredExistingTimeTables.map((tt) => tt.officeId));
		if (officeIds.length > 1) {
			console.error('Multiple Office IDs!');
			return Promise.resolve(false);
		}
		for (const timeTable of filteredExistingTimeTables) {
			if (!(timeTable.resourceId in byResource)) {
				byResource[timeTable.resourceId] = [];
			}
			byResource[timeTable.resourceId].push(timeTable);
		}
		return Promise.all(
			Object.keys(byResource).map((resourceId) => {
				const timetables = byResource[resourceId];
				if (!timetables.length) {
					return true;
				}
				const timetableRequest: Omit<IBookingTimeTable, 'bookingTemplateId'> = {
					resourceId,
					officeId: timetables[0].officeId,
					startTime: startTime.toISOString(),
					endTime: endTime.toISOString(),
					name: timetables[0].name,
				};

				const btIds = uniq(timetables.map((tt) => tt.bookingTemplateId));

				if (btIds.length > 0) {
					return createTimetables(dispatch, getState, timetableRequest, btIds);
				} else {
					return createUnavailableTimeTable(dispatch, getState, timetableRequest);
				}
			}),
		).then((results): boolean | Promise<boolean> => {
			if (results.some((r) => r === false)) {
				return false;
			}
			dispatch(requestStart());
			return Promise.all(
				filteredExistingTimeTables.map((tt) =>
					requestHelper(
						fetch(getState().fetchDefault.baseAPI + 'api/shop/ResourceTimeTables/' + tt.id, {
							...getState().fetchDefault.requestInit,
							method: 'DELETE',
						}),
					),
				),
			)
				.then(() => dispatch(getBookingTimetables()))
				.then(() => true)
				.catch((err) => {
					console.error(err);
					dispatch(requestFailure(err));
					return false;
				});
		});
	};
}

/** Action Creator */
export function requestStart(): IBookingTimetablesAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IBookingTimetablesAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function getBookingTimetablesSuccess(items: IBookingTimeTable[]): IBookingTimetablesAction {
	return {
		type: GET_BOOKINGTIMETABLES_SUCCESS,
		payload: {
			items,
		},
	};
}

/** Action Creator */
export function clearBookingTimetablesErrors(): IBookingTimetablesAction {
	return {
		type: CLEAR_ERRORS,
	};
}
